import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { formatDateTimeNL } from '@/lib/format';

const ACTION_COLORS = {
  login: 'bg-green-100 text-green-800',
  login_failed: 'bg-red-100 text-red-700',
  logout: 'bg-slate-100 text-slate-700',
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-amber-100 text-amber-800',
  delete: 'bg-red-100 text-red-700',
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', entity_type: '' });

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/audit-log?${params}`);
      setEntries(data.entries || []);
    } catch (e) {
      console.error('[audit-log] load failed', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="page-audit-log">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck size={18} /> Audit log
        </h2>
        <button
          onClick={load}
          data-testid="refresh-audit"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          <RefreshCw size={14} /> Vernieuwen
        </button>
      </div>

      <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <select
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          data-testid="filter-action"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Alle acties</option>
          <option value="login">Login</option>
          <option value="login_failed">Login mislukt</option>
          <option value="create">Aanmaken</option>
          <option value="update">Bijwerken</option>
          <option value="delete">Verwijderen</option>
        </select>
        <select
          value={filters.entity_type}
          onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
          data-testid="filter-entity"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Alle entiteiten</option>
          <option value="user">Gebruiker</option>
          <option value="location">Locatie</option>
          <option value="employee">Medewerker</option>
          <option value="roster_assignment">Roostershift</option>
          <option value="template">Standaardrooster</option>
        </select>
        <button onClick={load} className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light">
          Filteren
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Laden…</p>
        ) : entries.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">Geen audit-entries gevonden.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Tijdstip</th>
                <th className="px-4 py-2 text-left">Gebruiker</th>
                <th className="px-4 py-2 text-left">Actie</th>
                <th className="px-4 py-2 text-left">Entiteit</th>
                <th className="px-4 py-2 text-left">Locatie</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-xs text-slate-600">{formatDateTimeNL(e.timestamp)}</td>
                  <td className="px-4 py-2 text-slate-800">{e.user_email || e.user_id}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${ACTION_COLORS[e.action] || 'bg-slate-100'}`}>{e.action}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{e.entity_type}{e.entity_id ? ` · ${e.entity_id.substring(0, 8)}…` : ''}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{e.location_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
